import { Controller } from "@hotwired/stimulus"
import ApexCharts from "apexcharts";

export default class extends Controller {
  static targets = ["source", "canvas"]

  connect() {
    this.charts = [];
    this.observers = [];

    this.sourceTargets.forEach((sourceEl, index) => {
      const canvasEl = this.canvasTargets[index];
      if (!canvasEl) return;

      let chartData;
      try {
        chartData = JSON.parse(sourceEl.textContent);
      } catch (e) {
        console.error("Failed to parse chart data:", e);
        return;
      }

      const chartType = chartData.type || 'area';
      const options = this.#buildOptions(chartType, chartData);
      const chart = new ApexCharts(canvasEl, options);

      chart.render().then(() => {
        this.#fixFlowbiteFontSizeBug(canvasEl);
      });

      this.charts.push(chart);
    });
  }

  disconnect() {
    this.observers.forEach(obs => obs.disconnect());
    this.charts.forEach(chart => chart.destroy());
    this.charts = [];
    this.observers = [];
  }

  #buildOptions(chartType, chartData) {
    // A series can carry its own `type` (e.g. "line") to overlay it on an otherwise-area/bar chart.
    // ApexCharts renders mixed series types correctly as long as the base chart type is "line".
    const hasMixedSeriesTypes = Array.isArray(chartData.series) && chartData.series.some((s) => s && s.type);
    const baseChartType = (chartType === 'area' && hasMixedSeriesTypes) ? 'line' : chartType;

    const shared = {
      chart: {
        type: baseChartType,
        height: 200,
        toolbar: { show: false },
        zoom: { enabled: false }
      },
      series: chartData.series,
      colors: chartData.colors,
      grid: { borderColor: '#e5e7eb', strokeDashArray: 4 }
    };

    if (chartType === 'sparkline') {
      return {
        chart: {
          type: 'area',
          height: '100%',
          width: '100%',
          sparkline: { enabled: true },
          toolbar: { show: false }
        },
        series: chartData.series,
        colors: chartData.colors,
        stroke: { curve: 'smooth', width: 1.5, opacity: 0.12 },
        fill: {
          type: 'gradient',
          gradient: {
            shadeIntensity: 1,
            opacityFrom: 0.04,
            opacityTo: 0.008,
            stops: [0, 90, 100]
          }
        },
        tooltip: { enabled: false },
        xaxis: { labels: { show: false } },
        yaxis: { labels: { show: false }, min: 0 }
      };
    }

    if (chartType === 'donut') {
      return {
        chart: {
          type: 'donut',
          height: 200,
          toolbar: { show: false }
        },
        series: chartData.series,
        colors: chartData.colors,
        labels: chartData.labels,
        plotOptions: {
          pie: {
            donut: {
              size: '55%',
              labels: {
                show: true,
                name: { show: true, fontSize: '12px' },
                value: {
                  show: true,
                  fontSize: '18px',
                  fontWeight: 600,
                  formatter: (val) => val
                },
                total: {
                  show: true,
                  label: 'Total',
                  fontSize: '14px',
                  formatter: () => chartData.series.reduce((a, b) => a + b, 0)
                }
              }
            }
          }
        },
        dataLabels: { enabled: true },
        legend: {
          show: true,
          position: 'right',
          fontSize: '12px',
          fontWeight: 500,
          labels: { colors: '#374151' },
          markers: { size: 6 },
          itemMargin: { horizontal: 8, vertical: 4 }
        },
        tooltip: { enabled: true }
      };
    }

    if (chartType === 'sunburst') {
      return {
        chart: {
          type: 'sunburst',
          height: 200,
          toolbar: { show: false }
        },
        series: chartData.series,
        plotOptions: {
          sunburst: { innerSize: '25%', borderRadius: 5, spacing: 1 }
        },
        legend: {
          show: true,
          position: 'right',
          fontSize: '12px',
          fontWeight: 500,
          labels: { colors: '#374151' },
          markers: { size: 6 },
          itemMargin: { horizontal: 8, vertical: 4 }
        },
        tooltip: { enabled: true }
      };
    }

    if (chartType === 'bar') {
      return {
        ...shared,
        plotOptions: {
          bar: { horizontal: true, barHeight: '60%', borderRadius: 4 }
        },
        xaxis: {
          categories: chartData.categories,
          labels: { style: { fontSize: '12px' } }
        },
        yaxis: {
          labels: { style: { fontSize: '12px' } }
        },
        dataLabels: {
          enabled: true,
          style: { fontSize: '12px', fontWeight: 600 },
          offsetX: 4
        },
        legend: { show: false },
        tooltip: { enabled: true, y: { formatter: (val) => `${val}` } }
      };
    }

    if (chartType === 'stacked_bar') {
      return {
        ...shared,
        // 'stacked_bar' isn't a real ApexCharts type — bar + stacked + stackType '100%' is. Raw
        // counts go in; ApexCharts normalizes each row to percentages while the tooltip still
        // reads the original per-series counts.
        chart: { ...shared.chart, type: 'bar', stacked: true, stackType: '100%' },
        plotOptions: {
          bar: { horizontal: true, barHeight: '60%', borderRadius: 4 }
        },
        xaxis: {
          categories: chartData.categories,
          labels: { style: { fontSize: '12px' }, formatter: (val) => `${Math.round(val)}%` }
        },
        yaxis: { labels: { style: { fontSize: '12px' } } },
        dataLabels: { enabled: false },
        legend: {
          show: true,
          position: 'top',
          horizontalAlign: 'left',
          fontSize: '13px',
          labels: { colors: '#374151' },
          markers: { size: 6 }
        },
        tooltip: {
          enabled: true,
          y: { formatter: (val) => `${val} response${val === 1 ? '' : 's'}` }
        }
      };
    }

    if (chartType === 'grouped_bar') {
      return {
        ...shared,
        chart: { ...shared.chart, type: 'bar' }, // 'grouped_bar' isn't a real ApexCharts type — bar + horizontal:false is
        plotOptions: {
          bar: { horizontal: false, columnWidth: '55%', borderRadius: 4 }
        },
        xaxis: {
          categories: chartData.categories,
          labels: { style: { fontSize: '12px' } }
        },
        yaxis: { labels: { style: { fontSize: '12px' } } },
        dataLabels: { enabled: false },
        legend: {
          show: true,
          position: 'top',
          horizontalAlign: 'left',
          fontSize: '13px',
          labels: { colors: '#374151' },
          markers: { size: 6 }
        },
        tooltip: { enabled: true, shared: true, intersect: false }
      };
    }

    // Default: area chart. When series mix "line" and (implicit) "area" types, fill/stroke must be
    // given as per-series arrays — a single shared fill/stroke object applies gradient fill to every
    // series indiscriminately, including line-typed ones, which washes out their color entirely.
    // A series with no explicit type falls back to the chart's own declared type (not hardcoded
    // 'area') - otherwise an all-line chart (chartData.type: 'line', no per-series overrides) would
    // still get the thin, faded area treatment on every series. Uses the raw `chartType`, not the
    // mixed-type-upgraded `baseChartType`, so an actually-mixed chart (chartType left as 'area', one
    // series opting into 'line') doesn't flip its other, un-typed series to 'line' too.
    const seriesTypes = chartData.series.map((s) => (s && s.type) || (chartType === 'line' ? 'line' : 'area'));

    const options = {
      ...shared,
      legend: {
        show: true,
        position: 'top',
        horizontalAlign: 'left',
        fontSize: '14px',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        fontWeight: 500,
        labels: { colors: '#374151' },
        markers: { size: 6, offsetX: -2 },
        itemMargin: { horizontal: 12, vertical: 4 }
      },
      xaxis: {
        categories: chartData.categories,
        tickAmount: 8,
        labels: {
          rotate: -45,
          hideOverlappingLabels: true,
          style: { fontSize: '11px' }
        }
      },
      yaxis: { min: 0 },
      stroke: {
        curve: 'smooth',
        width: seriesTypes.map((t) => (t === 'line' ? 3 : 2))
      },
      dataLabels: { enabled: false },
      fill: {
        type: seriesTypes.map((t) => (t === 'line' ? 'solid' : 'gradient')),
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.4,
          opacityTo: 0.1,
          stops: [0, 90, 100]
        }
      },
      tooltip: { shared: true, intersect: false }
    };

    return this.#applyAverageAnnotations(this.#applyPercentOptions(options, chartData), chartData);
  }

  // Charts carrying chartData.averages (the 30-day cards, via DashboardPresenter#build_chart_data) get a
  // dashed horizontal line per series at its daily mean, color-matched to the series so it keys to the
  // legend. No text label: with 2-3 near-valued means per chart the labels collide, and the averages
  // strip rendered under the chart already carries the numbers. Charts without averages are untouched.
  #applyAverageAnnotations(options, chartData) {
    if (!Array.isArray(chartData.averages)) return options;

    const lines = chartData.averages
      .filter((avg) => avg && avg.value > 0) // a zero mean would just redraw the x-axis
      .map((avg) => ({
        y: avg.value,
        borderColor: avg.color,
        strokeDashArray: 5,
        opacity: 0.6
      }));

    if (lines.length === 0) return options;

    // Merge rather than assign - #applyPercentOptions may already have written annotations.xaxis.
    options.annotations = { ...options.annotations, yaxis: lines };

    return options;
  }

  // Percent-rate charts (chartData.percent) opt into a 0-100% y-axis, an "accepted/sent" tooltip, and a
  // dashed "Maturing" forecast band over the trailing month(s) whose 2-week acceptance window hasn't
  // fully elapsed. Count-based charts (every other caller) are returned untouched, byte-for-byte.
  #applyPercentOptions(options, chartData) {
    if (!chartData.percent) return options;

    // 0-100% y-axis with a "%" suffix on labels.
    options.yaxis = { min: 0, max: 100, labels: { formatter: (v) => `${v}%` } };

    // Tooltip that reads accepted/sent off the {x, y, sent, accepted} data point object alongside the rate.
    options.tooltip = {
      ...options.tooltip,
      y: {
        formatter: (val, { seriesIndex, dataPointIndex, w }) => {
          const point = w?.config?.series?.[seriesIndex]?.data?.[dataPointIndex];
          if (!point || typeof point !== 'object' || point.sent === undefined) return `${val}`;

          const rateLabel = (val === null || val === undefined) ? 'n/a' : `${val}%`;
          return `${rateLabel} (${point.accepted}/${point.sent})`;
        }
      }
    };

    // forecastDataPoints/annotations are top-level ApexCharts options (siblings of chart/series/xaxis).
    if (Number.isInteger(chartData.maturingCount) && chartData.maturingCount > 0) {
      const splitIndex = chartData.categories.length - chartData.maturingCount;
      options.forecastDataPoints = { count: chartData.maturingCount };
      options.annotations = {
        xaxis: [
          {
            x: chartData.categories[splitIndex],
            x2: chartData.categories[chartData.categories.length - 1],
            fillColor: '#f3f4f6',
            opacity: 0.5,
            label: { text: 'Maturing', style: { fontSize: '11px' } }
          }
        ]
      };
    }

    return options;
  }

  // Flowbite's Tailwind v4 plugin compiles font-size: 0.75rem to font-size: 0
  // for all ApexCharts text classes. The !important in a @layer beats CSS overrides,
  // so we must fix it via inline !important after render.
  #fixFlowbiteFontSizeBug(el) {
    const fixes = {
      '.apexcharts-legend-text': '0.875rem',
      '.apexcharts-tooltip-title': '0.875rem',
      '.apexcharts-tooltip-text-y-label': '0.75rem',
      '.apexcharts-tooltip-text-y-value': '0.75rem',
      '.apexcharts-xaxistooltip-text': '0.75rem'
    };

    for (const [selector, size] of Object.entries(fixes)) {
      el.querySelectorAll(selector).forEach(node => {
        node.style.setProperty('font-size', size, 'important');
      });
    }

    // Tooltip elements are created dynamically, so also observe for new ones
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node.nodeType !== Node.ELEMENT_NODE) continue;
          for (const [selector, size] of Object.entries(fixes)) {
            if (node.matches?.(selector)) {
              node.style.setProperty('font-size', size, 'important');
            }
            node.querySelectorAll?.(selector).forEach(child => {
              child.style.setProperty('font-size', size, 'important');
            });
          }
        }
      }
    });

    observer.observe(el, { childList: true, subtree: true });
    this.observers.push(observer);
  }
}
